import { api } from '@/Data/api';
import ss from '../Session';
import { RoomOrnament, RoomPlacement, WorldResponse, CharacterTask, UserCharacter, HomePictureTask, HomePicture } from './types';

export * from './types';

/**
 * List all ornaments
 * @param type Filter by type: 'ornament' or 'wallpaper'
 */
export const listOrnaments = async (type?: 'ornament' | 'wallpaper'): Promise<RoomOrnament[]> => {
    try {
        const res = await api.get<WorldResponse<RoomOrnament[]>>('/world/ornament', {
            params: type ? { type } : undefined,
            headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` }
        });
        return res.data?.code === 200 ? (res.data.data ?? []) : [];
    } catch (e) {
        console.error('Failed to list ornaments:', e);
        return [];
    }
};

/**
 * Get placements for a user's room
 * @param addressid User's public address ID
 */
export const getPlacements = async (addressid: string): Promise<RoomPlacement[]> => {
    try {
        const res = await api.get<WorldResponse<RoomPlacement | RoomPlacement[]>>('/world/placement', {
            params: { addressid },
            headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` }
        });
        if (res.data?.code !== 200) { return []; }
        const data = res.data.data;
        return Array.isArray(data) ? data : (data ? [data] : []);
    } catch (e) {
        console.error('Failed to get placements:', e);
        return [];
    }
};

/**
 * Update or place an ornament in user's room
 */
export const updatePlacement = async (params: {
    ornamentid: string;
    x: number;
    y: number;
    direction: 'right' | 'left';
}): Promise<RoomPlacement | null> => {
    try {
        const res = await api.put<WorldResponse<RoomPlacement>>('/world/placement', params, {
            headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` }
        });
        return res.data?.code === 200 ? (res.data.data ?? null) : null;
    } catch (e) {
        console.error('Failed to update placement:', e);
        return null;
    }
};

/**
 * Remove an ornament from user's room
 */
export const deletePlacement = async (ornamentid: string): Promise<boolean> => {
    try {
        const res = await api.delete<WorldResponse<any>>('/world/placement', {
            params: { ornamentid },
            headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` }
        });
        return res.data?.code === 200;
    } catch (e) {
        console.error('Failed to delete placement:', e);
        return false;
    }
};

/**
 * Get user's owned ornaments
 */
export const getOwnerships = async (): Promise<string[]> => {
    try {
        const res = await api.get<WorldResponse<{ ornamentid: string }[]>>('/world/ownership', {
            headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` }
        });
        if (res.data?.code !== 200) { return []; }
        return (res.data.data ?? []).map(o => o.ornamentid);
    } catch (e) {
        console.error('Failed to get ownerships:', e);
        return [];
    }
};

/**
 * Get user's character
 * @param addressid Optional target user's address ID
 */
export const getCharacter = async (addressid?: string): Promise<UserCharacter | null> => {
    try {
        const res = await api.get<WorldResponse<UserCharacter>>(
            '/world/character',
            {
                params: addressid ? { addressid } : undefined,
                headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` }
            }
        );

        if (res.data?.code !== 200) {
            return null;
        }

        return res.data.data ?? null;
    } catch (e) {
        console.error('Failed to get character:', e);
        return null;
    }
};

/**
 * Generate character from source image
 */
export const generateCharacter = async (params: {
    source_image?: string;
    addressid?: string;
    prompt?: string;
} = {}): Promise<string | null> => {
    try {
        const res = await api.put<WorldResponse<{ taskid: string }>>(
            '/world/character',
            params,
            { headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` } }
        );

        return res.data.data?.taskid ?? null;
    } catch (e) {
        console.error('Failed to generate character:', e);
        return null;
    }
};

/**
 * Get character generation task status
 */
export const getCharacterTask = async (taskid: string): Promise<CharacterTask | null> => {
    try {
        const res = await api.get<WorldResponse<CharacterTask>>(
            '/world/character/task',
            {
                params: { taskid },
                headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` }
            }
        );

        if (res.data?.code !== 200) {
            return null;
        }

        return res.data.data ?? null;
    } catch (e) {
        console.error('Failed to get character task:', e);
        return null;
    }
};

/**
 * Generate home picture (character placed in room)
 */
export const generateHomePicture = async (params: {
    room_image: string;
    addressid?: string;
    prompt?: string;
}): Promise<string | null> => {
    try {
        const res = await api.post<WorldResponse<{ taskid: string }>>(
            '/world/home-picture',
            params,
            { headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` } }
        );

        return res.data.data?.taskid ?? null;
    } catch (e) {
        console.error('Failed to generate home picture:', e);
        return null;
    }
};

/**
 * Get home picture generation task status
 */
export const getHomePictureTask = async (taskid: string): Promise<HomePictureTask | null> => {
    try {
        const res = await api.get<WorldResponse<HomePictureTask>>(
            '/world/home-picture/task',
            {
                params: { taskid },
                headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` }
            }
        );

        if (res.data?.code !== 200) {
            return null;
        }

        return res.data.data ?? null;
    } catch (e) {
        console.error('Failed to get home picture task:', e);
        return null;
    }
};

/**
 * List user's generated home pictures
 * @param addressid Optional target user's address ID
 */
export const listHomePictures = async (addressid?: string): Promise<HomePicture[]> => {
    try {
        const res = await api.get<WorldResponse<HomePicture[]>>(
            '/world/home-picture',
            {
                params: addressid ? { addressid } : undefined,
                headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` }
            }
        );

        if (res.data?.code !== 200) {
            return [];
        }

        return res.data.data ?? [];
    } catch (e) {
        console.error('Failed to list home pictures:', e);
        return [];
    }
};

/**
 * Delete home pictures by image URLs
 * @param images Array of image URLs to delete
 * @returns Number of deleted pictures
 */
export const deleteHomePictures = async (images: string[]): Promise<number> => {
    try {
        const res = await api.delete<WorldResponse<{ deleted: number }>>(
            '/world/home-picture',
            {
                params: { images: images.join(',') },
                headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` }
            }
        );

        if (res.data?.code !== 200) {
            return 0;
        }

        return res.data.data?.deleted ?? 0;
    } catch (e) {
        console.error('Failed to delete home pictures:', e);
        return 0;
    }
};
